import { parseLoginResponse } from '@drama/contracts'

/**
 * @typedef {import('@drama/contracts/platform').LoginRequestT} LoginRequest
 * @typedef {import('@drama/contracts/platform').LoginResponseT} LoginResponse
 * @typedef {import('@drama/contracts/platform').RegisterRequestT} RegisterRequest
 * @typedef {import('@drama/contracts/platform').SendRegistrationCodeRequestT} SendRegistrationCodeRequest
 * @typedef {import('@drama/contracts/platform').OauthLoginRequestT} OauthLoginRequest
 */

/**
 * auth 域：薄转发（刷新轮转策略归 @drama/auth-client，本包只发请求）。
 * 全部为公开端点（不带 Authorization），除 mergeAnonymous 需当前匿名身份头。
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 */
export function createAuthApi(transport) {
  return {
    /**
     * @param {SendRegistrationCodeRequest} req
     * @returns {Promise<{ ok: true }>}
     */
    sendCode(req) {
      return transport.request('POST', '/api/sk/auth/send-code', { body: req, public: true })
    },

    /**
     * @param {{ email:string, locale?: string }} req
     * @returns {Promise<{ ok: true }>}
     */
    requestPasswordReset(req) {
      return transport.request('POST', '/api/sk/auth/password-reset/request', { body: req, public: true })
    },

    /**
     * @param {{ email:string, password:string, verificationCode?: string, code?: string }} req
     * @returns {Promise<{ ok: true }>}
     */
    confirmPasswordReset(req) {
      return transport.request('POST', '/api/sk/auth/password-reset/confirm', { body: req, public: true })
    },

    /**
     * @param {LoginRequest} req
     * @returns {Promise<LoginResponse>}
     */
    login(req) {
      return transport.request('POST', '/api/sk/auth/login', {
        body: req,
        public: true,
        parse: parseLoginResponse,
      })
    },

    /**
     * 邮箱验证码登录：邮箱不存在即建号，响应与登录同形。
     * @param {import('@drama/contracts/platform').EmailCodeLoginRequestT} req
     * @returns {Promise<LoginResponse>}
     */
    loginWithEmailCode(req) {
      return transport.request('POST', '/api/sk/auth/email-code/login', {
        body: req,
        public: true,
        parse: parseLoginResponse,
      })
    },

    /**
     * @param {RegisterRequest} req
     * @returns {Promise<LoginResponse>}
     */
    register(req) {
      return transport.request('POST', '/api/sk/auth/register', {
        body: req,
        public: true,
        parse: parseLoginResponse,
      })
    },

    /**
     * @param {OauthLoginRequest} req
     * @returns {Promise<LoginResponse>}
     */
    oauthLogin(req) {
      return transport.request('POST', '/api/sk/auth/oauth/login', {
        body: req,
        public: true,
        parse: parseLoginResponse,
      })
    },

    /**
     * @param {{ refresh_token:string }} req
     * @returns {Promise<LoginResponse>}
     */
    refresh(req) {
      return transport.request('POST', '/api/sk/auth/refresh', {
        body: req,
        public: true,
        parse: parseLoginResponse,
      })
    },

    /**
     * @param {{ refresh_token:string }} req
     * @returns {Promise<{ ok: true }>}
     */
    logout(req) {
      return transport.request('POST', '/api/sk/auth/logout', {
        body: req,
        public: true,
      })
    },

    /**
     * @returns {Promise<{ ok: true }>}
     */
    logoutAll() {
      return transport.request('POST', '/api/sk/auth/logout-all')
    },

    /**
     * 把匿名身份的观看进度/历史合并进已登录账户。
     * @param {{ anonymous_id:string }} req
     * @returns {Promise<any>}
     */
    mergeAnonymous(req) {
      return transport.request('POST', '/api/sk/auth/merge-anonymous', { body: req })
    },
  }
}
